import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Award, BadgeCheck, Check, FileUp, PartyPopper, Rocket, Users, UserPlus, X } from "lucide-react";

/**
 * First-run orientation for school admins. Answers "I landed here — now what?"
 * with a short setup journey whose climax is getting students in.
 *
 * Three CORE steps drive the progress bar and the "launch" moment:
 *   1. Add your team               — Manage Staff ("It's just me" to skip)
 *   2. Confirm your school profile — Settings
 *   3. Get your students in        — import / invite / share join code (the big one)
 *
 * Team is deliberately FIRST: staff should be in place before the roster
 * arrives so colleagues can share the onboarding work and everyone starts
 * with the right role. It keeps its "It's just me" escape for solo admins,
 * but is no longer framed as optional/secondary.
 *
 * Hitting 3/3 = the school is "live". Issuing the first certificate is then
 * surfaced as a "What's next" nudge BELOW the bar — valuable, but it shouldn't
 * gate the launch celebration, so it doesn't count toward progress.
 *
 * Steps mark themselves done from real signals passed in by the parent
 * (dashboard stats, a cert probe, a staff probe, a profile-confirmed flag).
 * Purely presentational: the parent (Dashboard) owns visibility + the persisted
 * dismiss/skip/confirm flags, so the card can be auto-shown on first run,
 * dismissed, and re-opened on demand.
 */
const GettingStartedChecklist = ({
  stats,
  profileConfirmed = false,
  certIssued = false,
  hasTeam = false,
  staffSkipped = false,
  onDismiss,
  onConfirmProfile,
  onInvite,
  onImport,
  onShareJoin,
  onSkipTeam,
  // Compact = narrow right-rail layout: action buttons stack under the step
  // text (instead of sitting inline on the right) so the card fits a sidebar.
  compact = false,
  // ADMIN+ gets the stand-up-the-school journey. Teachers/counselors can't
  // reach /manage-staff or edit school Settings (both ADMIN+), so promising
  // those steps sent them to dead ends — they get a journey of things their
  // rank actually permits instead.
  isAdmin = true,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const hasStudents = (stats.totalStudents || 0) > 0 || (stats.pendingInvites || 0) > 0;
  const school = stats.schoolName || t("gettingStarted.schoolFallback", { defaultValue: "Your school" });
  const doneLabel = t("gettingStarted.done", { defaultValue: "Done" });

  // ── Step definitions ──────────────────────────────────────────────────────
  // Composed into a journey below; which steps apply depends on rank.
  // For ADMIN+, team comes FIRST: staff should be in place before the roster
  // arrives, so colleagues can share the onboarding work and each person lands
  // with the right role from day one. Adding students first means re-doing
  // permissions and support work later.
  const adminSteps = [
    {
      key: "team",
      done: hasTeam || staffSkipped,
      doneLabel: hasTeam ? doneLabel : t("gettingStarted.steps.team.skipped", { defaultValue: "Just me" }),
      icon: Users,
      title: t("gettingStarted.steps.team.title", { defaultValue: "Add your team" }),
      desc: t("gettingStarted.steps.team.desc", {
        defaultValue:
          "Start here — onboarding goes faster when counselors and teachers share the load, and everyone lands with the right role before students arrive. Running it solo? Mark it not applicable.",
      }),
      actions: [
        {
          label: t("gettingStarted.steps.team.invite", { defaultValue: "Invite staff" }),
          icon: UserPlus,
          onClick: () => navigate("/manage-staff"),
          primary: true,
        },
        {
          label: t("gettingStarted.steps.team.skip", { defaultValue: "It's just me" }),
          onClick: onSkipTeam,
          subtle: true,
        },
      ],
    },
    {
      key: "profile",
      done: profileConfirmed,
      icon: BadgeCheck,
      title: t("gettingStarted.steps.profile.title", { defaultValue: "Confirm your school profile" }),
      desc: t("gettingStarted.steps.profile.desc", {
        defaultValue: "We pulled this from your signup — just confirm your grades and details and you're set.",
      }),
      actions: [
        {
          label: t("gettingStarted.steps.profile.confirm", { defaultValue: "Confirm profile" }),
          icon: BadgeCheck,
          onClick: () => {
            onConfirmProfile?.();
            navigate("/settings");
          },
          primary: true,
        },
      ],
    },
  ];

  // Shared by both journeys — inviting students is open to every staff rank.
  const studentsStep = {
    key: "students",
    done: hasStudents,
    icon: Rocket,
    title: t("gettingStarted.steps.students.title", { defaultValue: "Get your students in" }),
    desc: t("gettingStarted.steps.students.desc", {
      defaultValue:
        "This is the big one — the moment students start building. Import a class roster, invite them by email, or share your join code.",
    }),
    actions: [
      {
        label: t("gettingStarted.steps.students.import", { defaultValue: "Import roster" }),
        icon: FileUp,
        onClick: onImport,
      },
      {
        label: t("gettingStarted.steps.students.invite", { defaultValue: "Invite students" }),
        icon: UserPlus,
        onClick: onInvite,
        primary: true,
      },
      {
        label: t("gettingStarted.steps.students.share", { defaultValue: "Share join code" }),
        onClick: onShareJoin,
        subtle: true,
      },
    ],
  };

  // Teachers/counselors: view the roster they can already reach.
  const rosterStep = {
    key: "roster",
    done: hasStudents,
    icon: Users,
    title: t("gettingStarted.steps.roster.title", { defaultValue: "Meet your students" }),
    desc: t("gettingStarted.steps.roster.desc", {
      defaultValue:
        "See who's on your roster and how they're progressing — who's started building, who needs a nudge.",
    }),
    actions: [
      {
        label: t("gettingStarted.steps.roster.go", { defaultValue: "View students" }),
        icon: Users,
        onClick: () => navigate("/manage-students"),
        primary: true,
      },
    ],
  };

  // Certificate issuing is ungated — every staff rank can do it — so for
  // sub-admin staff it's a core step rather than a post-launch nudge.
  const certificateStep = {
    key: "certificates",
    done: certIssued,
    icon: Award,
    title: t("gettingStarted.steps.certificates.title", { defaultValue: "Issue your first certificate" }),
    desc: t("gettingStarted.steps.certificates.desc", {
      defaultValue:
        "Recognise student performance with graduation, honour-roll or custom certificates. Each one is publicly verifiable and lands in the student's Vault.",
    }),
    actions: [
      {
        label: t("gettingStarted.steps.certificates.go", { defaultValue: "Go to Certificates" }),
        icon: Award,
        onClick: () => navigate("/certificates"),
        primary: true,
      },
    ],
  };

  // ── Core steps (drive the progress bar + launch) ──────────────────────────
  const coreSteps = isAdmin ? [...adminSteps, studentsStep] : [studentsStep, rosterStep, certificateStep];

  const completed = coreSteps.filter((s) => s.done).length;
  const total = coreSteps.length;
  const live = completed === total;

  // ── "What's next" — surfaced only once live, never gates the launch ───────
  // Admin-only: for sub-admin staff the certificate is already a core step,
  // so re-surfacing it below the bar would duplicate it.
  const certStep = certificateStep;
  const showCertNext = isAdmin && live && !certIssued;

  const subtitle = live
    ? t("gettingStarted.liveSubtitle", { defaultValue: "{{school}} is live — students can join and start building now.", school })
    : completed === 0
      ? t("gettingStarted.startSubtitle", {
          defaultValue: "Let's get {{school}} ready. Three short steps — most admins finish in under 8 minutes.",
          school,
        })
      : t("gettingStarted.progressSubtitle", { defaultValue: "You're {{completed}} of {{total}} there — keep going.", completed, total });

  const renderActions = (step) =>
    step.actions.map((action) => {
      const ActionIcon = action.icon;
      return (
        <button
          key={action.label}
          type="button"
          onClick={action.onClick}
          className={
            action.subtle
              ? "inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-semibold text-gray-500 hover:text-gray-700 hover:underline underline-offset-2 transition-colors"
              : `inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                  action.primary
                    ? "bg-primary text-white hover:bg-blue-600"
                    : "border border-gray-300 text-gray-700 hover:border-primary hover:text-primary"
                }`
          }
        >
          {ActionIcon ? <ActionIcon size={14} /> : null}
          {action.label}
        </button>
      );
    });

  const renderStep = (step) => {
    const Icon = step.icon;
    return (
      <div
        key={step.key}
        className={`flex items-start gap-3 rounded-xl border p-3.5 transition-colors ${
          step.done ? "border-emerald-100 bg-emerald-50/40" : "border-gray-100 bg-white"
        }`}
      >
        {/* Status indicator */}
        <div
          className={`mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full ${
            step.done ? "bg-emerald-500 text-white" : "bg-blue-50 text-blue-600"
          }`}
        >
          {step.done ? <Check size={16} /> : <Icon size={15} />}
        </div>

        <div className="min-w-0 flex-1">
          <p className={`text-sm font-semibold ${step.done ? "text-gray-500 line-through" : "text-gray-900"}`}>
            {step.title}
            {step.optional ? (
              <span className="ml-2 align-middle text-xs font-medium text-gray-400">
                {t("gettingStarted.optional", { defaultValue: "· optional" })}
              </span>
            ) : null}
          </p>
          {!step.done ? <p className="text-xs text-gray-500 mt-0.5">{step.desc}</p> : null}
          {/* Compact (narrow-rail) layout stacks the actions under the text so
              they wrap instead of overflowing a sidebar column. */}
          {compact && !step.done ? <div className="mt-2 flex flex-wrap items-center gap-1.5">{renderActions(step)}</div> : null}
        </div>

        {/* Wide layout: actions inline on the right. Done steps always show the
            "Done" label (both layouts). */}
        {step.done ? (
          <span className="text-xs font-semibold text-emerald-600 flex-shrink-0 mt-1">{step.doneLabel || doneLabel}</span>
        ) : !compact ? (
          <div className="flex items-center gap-1.5 flex-shrink-0">{renderActions(step)}</div>
        ) : null}
      </div>
    );
  };

  return (
    <div
      id="getting-started"
      className="rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50/70 to-white p-5 flex flex-col gap-4 font-manrope scroll-mt-6"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h3 className="text-gray-900 text-lg font-bold">
            {live
              ? t("gettingStarted.liveTitle", { defaultValue: "{{school}} is ready 🎉", school })
              : t("gettingStarted.title", { defaultValue: "Getting started" })}
          </h3>
          <p className="text-sm text-gray-500">{subtitle}</p>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="text-gray-400 hover:text-gray-600 transition-colors -mt-1 -mr-1 p-1"
          title={t("gettingStarted.dismiss", { defaultValue: "Hide getting started" })}
          aria-label={t("gettingStarted.dismiss", { defaultValue: "Hide getting started" })}
        >
          <X size={18} />
        </button>
      </div>

      {/* Progress bar — over the core steps only. */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-2 rounded-full bg-blue-100 overflow-hidden">
          <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${(completed / total) * 100}%` }} />
        </div>
        <span className="text-xs font-semibold text-gray-500 whitespace-nowrap">
          {t("gettingStarted.progress", { defaultValue: "{{completed}} of {{total}} done", completed, total })}
        </span>
      </div>

      {live ? (
        <div className="flex items-center gap-2 rounded-xl border border-emerald-100 bg-emerald-50/50 px-4 py-3 text-sm font-semibold text-emerald-700">
          <PartyPopper size={16} />
          {t("gettingStarted.liveBanner", {
            defaultValue: "{{school}} is live — students can join with your code and start building today.",
            school,
          })}
        </div>
      ) : null}

      <div className="flex flex-col gap-2">{coreSteps.map(renderStep)}</div>

      {/* What's next — a forward nudge once the school is live. */}
      {showCertNext ? (
        <div className="flex flex-col gap-2 border-t border-blue-100 pt-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
            {t("gettingStarted.nextLabel", { defaultValue: "What's next" })}
          </p>
          {renderStep(certStep)}
        </div>
      ) : null}
    </div>
  );
};

export default GettingStartedChecklist;
